import { assertTurnTickContract } from './tick-contract.js';
import { isSelfStatusOrigin, statusOriginDefinitionId } from '../statuses/status-origin.js';
import { passesDbGate } from '../reactions/trigger-dispatch.js';
// Dice and charge work at a main-turn boundary. The shared ability handlers own the
// damage and charge mutations; this file only selects the legacy turn-hook sources.
import { type JanusCtx, type JanusReaders, type LogRow, type Row, type StatusRow, type StatusTriggerListener,
  HandBack, Refusal, ec, ecChanges, ecId, ecLean, handBack, insertRoll, int, itemField, record, requiredInt, scalar, upper } from '../ability/context.js';
import { consentAllows, consentPending, isPlayerController, TRIGGER_CONSENT_REASON } from '../ability/trigger-consent.js';
import { damageTypeRoll } from '../ability/rolls/damage-type-roll.js';
import { contestingRoll } from '../ability/rolls/contesting-roll.js';
import { variableDice } from '../ability/variable-amount.js';
import { adjustCharges } from '../ability/charges/adjust-charges.js';
import { abilityChargeEntry } from '../ability/charges/charge-entry.js';
import { rollDiceFaces } from '../ability/rolls/roll-dice-arr.js';
import { healingStatusChanges } from '../ability/rolls/healing-roll.js';
import { janus } from '../ability/janus.js';
import { removeStoredStatus, removeLoneConcentration } from '../ability/statuses/status-effect-removal-helpers.js';
import { orbEndOfTurnPass, orbStartOfTurnChannelPass } from '../ability/orbs/orb-cast.js';
import { runTriggerFamily, triggerFamilyPreDraw } from '../ability/trigger-executor.js';
import type { StartOfTurnResult } from './end-turn.js';
import { selectSkillForContestingRoll } from '../ability/select-skill-for-contesting-roll.js';
import { contestDirectionalCover } from '../ability/cover/directional-cover.js';
import { CONTEST_ROLL_ID, rollTypeModifierStacks, skillRollArr } from '../ability/skill-roll.js';
import { checkOpposingTeam } from '../ability/check-opposing-team.js';
import { type RollSource } from '../actions/roll-source.js';
import { grantTemporaryVitality } from '../ability/rolls/temp-v-roll.js';
import { TEMP_REGENERATION_ROLL_TYPE, REGENERATION_ROLL_TYPE, DOT_ROLL_TYPE, LINK_STATUS_IDS, removeStatusFromRow, statusCasterId, statusItemId, statusPaused, statusRows, statusRemovalBefore, type StatusRemoval } from '../statuses/expiry.js';
import { statusInputSnapshot } from './input-snapshots.js';
import { frameCopyOnRead } from '../ability/frame-snapshot.js';
import { applyZoneRadiusChanges, reconcileEndOfTurnZoneExits, runEndOfTurnZones, type ZoneEndPlan, type ZoneRadiusChange } from '../zones/end-of-turn-zones.js';
import { regenerationEntryTicks } from './regeneration-entry-gate.js';
import { carrierNamesRollType, withStatusTickExtreme } from '../ability/statuses/forced-roll-extreme.js';
import { detonateExpiredStatus } from '../ability/statuses/status-expiry-detonation.js';
import { skipUnported, unportedSkipRow } from '../unported.js';
import { runMasteryUpkeep, runMaintainedUpkeep, runMaintainedExpiry } from './k4-upkeep.js';
import { runDyingStratum } from './k4-dying.js';
import { decayQuantityStatus } from './k4-decay.js';
import { applyStatusFrame, statusRemovalFrame, skillsWithoutStatuses, type EndTurnRowFrame } from './status-frame.js';
import { mergeSpawnPlans, spawnPlanHasEffects, type SpawnRowPlan } from '../spawn/row-plan.js';
import { resolveFormulaVariables } from '../compile/formula-variable-resolution.js';
import formulaContract from '../compile/c1-contract.json';
import { phpIntval } from '../rolls/dice-roll.js';
import { readItemField } from '../compile/item-field-reader.js';
import { statusRemovalRuntime, applyStatusRemovalRuntime } from '../compile/status-removal-runtime.js';
import { heldInventoryRows } from '../statuses/gm-boundary.js';
import { statusScalar } from '../actions/activity.js';

/** Restart a synthetic pass with this effect disabled; discard every partial mutation. */
class TickEffectSkip extends Error {
  constructor(readonly effect: string, readonly reason: string) { super(reason); }
}
type TickEffect = <T>(key: string, work: () => T) => T | undefined;

export interface TurnTickReaders { ability: JanusReaders; roll: RollSource }
export interface TurnTickEntry { id: string; type: string; definition: Row | null; charges: number | null; max: number | null; inactive: boolean }
export interface TurnTickResult {
  /** Death and Dying use the shared browser row plan in the same saved tick. */
  spawnRows?: SpawnRowPlan;
  /** Browser-derived expiry projections, relayed verbatim with the saved status mutations. */
  statusFrames?: Record<string, EndTurnRowFrame>;
  statusTriggerListeners?: StatusTriggerListener[];
  teleportOffers?: JanusCtx['teleportOffers'];
  rows: LogRow[];
  ec: Record<string, Record<string, unknown>>;
  statusesAdded: StatusRow[];
  statusesRemoved: Array<{ status_item_id: string; target: string; status_id?: string; caster?: string; before?: Row }>;
  statusesChanged: Array<{ status_id: string; status_stack: number }>;
  radiusChanges?: ZoneRadiusChange[];
  charges: Array<{ holder: string; item_id: string; charges: number }>;
  /** OWNER RULING 2026-10-03 (ability/trigger-consent.ts deferFortunateOffer): player-controlled members whose luck gain inside a tick was not
   *  spent on Fortunate inline — the End Turn page offers it after the commit (a page-side field, stripped before posting). */
  fortunateOffers?: string[];
  /** The reset and START expiries derived after the end phase; callers post this block. */
  start?: StartOfTurnResult;
  /** Internal END-to-START selection, consumed before persistence. */
  successor?: { id: string; wrap: boolean };
}
export interface TurnTickInput {
  /** Synthetic OoC round: status lifecycle only; no pointer or resource refresh. */
  outOfCombat?: boolean;
  /** Legendary windows have no START lifecycle; draining one resumes a normal START. */
  skipStart?: boolean;
  resumedMain?: boolean;
  endingEntries?: TurnTickEntry[];
  /** A13: isolated browser orb work after a legacy boundary, without replaying its other hooks. */
  orbOnly?: 'pass' | 'channel';
  preflightOnly?: boolean;
  encounter: Row;
  roster: Row[];
  /** null = START MODE (resolveStartTurnTicks): the first combatant's start-of-turn hooks alone — no ending actor, no end_turn row. */
  endingId: string | null;
  nextId: string;
  nextAfterEnd?: (roster: Row[]) => { id: string; wrap: boolean };
  entriesFor?: (row: Row) => TurnTickEntry[];
  nextReaction: 0 | 1;
  endRemoved: ReadonlySet<string>;
  startRemoved: ReadonlySet<string>;
  endExpiries?: readonly StatusRemoval[];
  startExpiries?: readonly StatusRemoval[];
  entries: TurnTickEntry[];
  read: TurnTickReaders;
  deriveStart?: (next: Row, roster: Row[], successor?: { id: string; wrap: boolean }) => StartOfTurnResult;
  zones?: ZoneEndPlan;
  /** Adapter prerequisites run on every synthetic outcome before real dice. */
  prepare?: (result: TurnTickResult) => void;
  prepareEnd?: (result: TurnTickResult) => void;
  /** A caller that resolves the SAME tick input several times with only `read.roll` differing (the combat start's first-turn block: eight
   *  synthetic outcomes, then the real dice — combat/start-roll.ts) shares one object here: the dry phase, which never reads `read.roll`,
   *  runs once and records the effects it froze into skips; every later call starts from that verdict (fix-bug 2026-10-13: the start ran the
   *  identical dry phase nine times). Ignored when the input carries `prepare` / `prepareEnd` (their side effects run per dry outcome). */
  preflight?: { skipped?: ReadonlyMap<string, string> };
}
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const TURN_RECHARGE_ROLL = '52949AF2-7C18-4BA3-754E-4BF898A67AFD';
const REGENERATION_ROLL = REGENERATION_ROLL_TYPE;
const DAMAGE_ROLL = '77A044B7-7041-CA96-3C63-BB718B15A364';
const CONCENTRATING = 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B';
const PSI = '6A8183E1-349E-3960-B465-6C863097E193';
const FURY = 'CA6567EA-74E1-41CC-9C7C-E8EEA8C0B6D6';
const FORTUNATE = 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8';

function statusDefinition(ctx: JanusCtx, status: Row): Row {
  const nested = record(status.status_arr);
  const found = nested && (record(nested.items) || record(nested.item_layer_types)) ? nested : ctx.read.definition(statusItemId(status));
  if (!found) handBack(`turn-tick:status-definition-not-held:${upper(statusScalar(status, 'status_id'))}:${statusItemId(status)}`);
  return found;
}

function damageOverTime(ctx: JanusCtx, ending: Row, status: Row, definition: Row,
  changed: TurnTickResult['statusesChanged']): boolean {
    if (upper(ctx.read.value(definition, 'roll_type')) !== DOT_ROLL_TYPE) return false;
    if (ctx.read.has(definition, 'target_retries_contesting_roll_on_end_of_turn')) return false;
    if (Object.keys(record(definition.item_layer_types) ?? {}).some(key => key.startsWith('if_')))
      handBack('turn-tick:dot-conditions');
    if (Number(status.status_aoe_source ?? 0) !== 0 || ctx.read.has(definition, 'persistent_aoe'))
      handBack('turn-tick:dot-zone');
    const casterId = statusCasterId(status);
    const caster = ctx.members.get(casterId);
    if (!caster) handBack('turn-tick:dot-caster-not-held');
    let stack = requiredInt(status.status_stack ?? 1, 'turn-tick:status-stack-not-held');
    const name = String(itemField(definition, 'item_name') ?? '').toLowerCase();
    if (name === 'burning' || name === 'corroding') {
      stack = decayQuantityStatus(ctx, ending, status, name, changed);
      if (stack === 0) return true;
    }
    const ability = clone(definition);
    const layers = record(ability.item_layer_types);
    if (!layers) handBack('turn-tick:dot-layers-not-held');
    // A lingering status owns its damage. Its origin contributes only a contest;
    // replaying the origin would deal the spell's damage and apply its statuses again.
    if (!ctx.read.has(ability, 'skill_roll_contestee')) {
      const origin = statusTickOrigin(ctx, status, definition, 'turn-tick:dot-contest-or-origin');
      if (origin && ctx.read.has(origin, 'skill_roll_contestee')) {
        layers.skill_roll_contestee = ctx.read.entries(origin, 'skill_roll_contestee');
        for (const key of ['skill_roll_contester', 'ability_dc', 'contesting_roll_successful_for_half_damage'])
          if (ctx.read.has(origin, key)) layers[key] = key === 'skill_roll_contester'
            ? ctx.read.entries(origin, key) : ctx.read.value(origin, key);
      }
    }
    if (ctx.read.has(ability, 'damage_rolls')) handBack('turn-tick:dot-multi-damage');
    if (!ctx.read.has(ability, 'dice') || !ctx.read.has(ability, 'die') || !ctx.read.has(ability, 'damage_type'))
      handBack('turn-tick:dot-damage-shape');
    const dice = requiredInt(variableDice(ctx.read.value(ability, 'dice'), 'damage_only'), 'turn-tick:dot-dice');
    layers.dice = dice * stack;
    const contest = ctx.read.has(ability, 'skill_roll_contestee');
    const input = { casterId, targetIds: [ecId(ending)], abilityId: statusItemId(status), rawDiceMod: 0,
      rawDiceAdvDisStacks: 0, useAbilityVariable1: contest ? 'dot_status_tick' : 'damage_only', useAbilityVariable2: null };
    const priorConnection = ctx.statusConnectedId;
    ctx.statusConnectedId = String(statusScalar(status, 'status_connected_id') ?? '') || null;
    try {
      withStatusTickExtreme(ctx, status, definition, caster, () => {
        if (contest) contestingRoll(ctx, input, ability, caster, [ending]);
        else damageTypeRoll(ctx, input, ability, caster, ending);
      });
    } finally { ctx.statusConnectedId = priorConnection; }
    return true;
}

/** check_statuses_for_this...as_a_target: contesting-roll retry is status-only.
 * The first eligible connection consumes this end-of-turn retry pass. */
function contestRetry(ctx: JanusCtx, ending: Row, status: Row, ability: Row): boolean {
    if (!ctx.read.has(ability, 'target_retries_contesting_roll_on_end_of_turn')) return false;
    const casterId = upper(status.status_caster_id);
    if (!casterId || casterId === ecId(ending)) return false;
    const caster = ctx.members.get(casterId);
    if (!caster) handBack('turn-tick:contest-caster-not-held');
    const rollType = upper(ctx.read.value(ability, 'roll_type'));
    if (![CONTEST_ROLL_ID, DAMAGE_ROLL].includes(rollType)
        || !ctx.read.has(ability, 'skill_roll_contestee'))
      handBack('turn-tick:contest-retry-roll-type');
    if (ctx.read.has(ability, 'ability_dc') || Number(status.status_forced_roll_extreme ?? 0) !== 0)
      handBack('turn-tick:contest-retry-fixed-dc-or-extreme');
    if (Number(status.status_aoe_source ?? 0) !== 0)
      handBack('turn-tick:contest-retry-zone');
    const input = { casterId, targetIds: [ecId(ending)], abilityId: statusItemId(status), rawDiceMod: 0,
      rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null };
    const casterSkill = selectSkillForContestingRoll(ctx, caster, ctx.read.entries(ability, 'skill_roll_contester'));
    const casterResult = skillRollArr(ctx, input, caster, casterSkill, null, 0);
    // damage_roll.php tests team eligibility after the caster's contest row.
    if ((ctx.read.has(ability, 'effect_only_affects_opposing_team_type') && !checkOpposingTeam(ctx, caster, ending))
        || (ctx.read.has(ability, 'effect_only_affects_same_team_type') && checkOpposingTeam(ctx, caster, ending))) return true;
    const targetSkill = selectSkillForContestingRoll(ctx, ending, ctx.read.entries(ability, 'skill_roll_contestee'));
    const cover = contestDirectionalCover(ctx, ability, caster, ending, targetSkill.id);
    targetSkill.adv += cover.advStacks;
    const targetResult = skillRollArr(ctx, input, ending, targetSkill, caster, casterResult);
    // A contest-typed retry only repeats the save. Damage-typed retries go
    // through damage_roll.php, including the half-damage-on-success branch.
    if (rollType === DAMAGE_ROLL && ctx.read.has(ability, 'damage_type')
        && ((!cover.autoWin && casterResult >= targetResult) || ctx.read.has(ability, 'contesting_roll_successful_for_half_damage')))
      damageTypeRoll(ctx, input, ability, caster, ending, cover.autoWin || casterResult < targetResult);
    if (cover.autoWin || casterResult < targetResult) {
      removeStoredStatus(ctx, ending, status);
      insertRoll(ctx, { roll_type_1: 'status_effect_removed_after_contesting_roll_passed',
        roll_item_id: statusItemId(status), by: ending, to: ending });
    }
    return true;
}

/** PHP walks each target status once: DoT then retry, with only the retry consumed. */
function endTargetTicks(ctx: JanusCtx, ending: Row, removed: ReadonlySet<string>,
  changed: TurnTickResult['statusesChanged'], expiries: readonly StatusRemoval[] = [], effect: TickEffect = (_key, work) => work(), zones?: ZoneEndPlan): void {
  let retryConsumed = false;
  for (const status of statusRows(ending, 'target')) {
    if (statusPaused(status)) continue;
    effect('end-status:' + upper(status.status_id), () => {
    if (removed.has(upper(status.status_id))) {
      // R-176: a detonating row (expiry.ts flagged it on its target_end removal) rolls its damage right after its expiry row.
      const expiry = expiries.find(e => upper(e.status_id) === upper(status.status_id));
      const definition = expiry?.detonates && expiry.family === 'target_end' ? statusDefinition(ctx, status) : null;
      expiryTriggers(ctx, new Set([upper(status.status_id)]), expiries);
      if (definition) detonateExpiredStatus(ctx, ending, status, definition, statusItemId(status));
      return;
    }
    const definition = statusDefinition(ctx, status);
    // The judged zone hook owns its anchor/occupant damage. It must not also be treated as an unavailable target DoT.
    const zoneStatus = Number(status.status_aoe_source ?? 0) !== 0 || ctx.read.has(definition, 'persistent_aoe');
    const origin = upper(status.status_origin_id) || statusItemId(status), caster = statusCasterId(status);
    if (zoneStatus && zones && (Number(status.status_aoe_source ?? 0) !== 0
      || zones.dispatches.some(step => step.abilityId === origin && step.casterId === caster && step.connectedId === upper(status.status_connected_id))
      || zones.claims.some(claim => claim.item_id === origin && claim.caster_id === caster && claim.target_id === ecId(ending)))) return;
    if (damageOverTime(ctx, ending, status, definition, changed)) return;
    if (!retryConsumed) retryConsumed = contestRetry(ctx, ending, status, definition);
    });
  }
}

function statusTickOrigin(ctx: JanusCtx, status: Row, definition: Row, missingReason = 'regeneration-origin-not-held'): Row | null {
  if (isSelfStatusOrigin(upper(statusScalar(status, 'status_id')), upper(statusScalar(status, 'status_origin_id')))) return null;
  const nested = record(statusScalar(status, 'status_origin_arr'));
  if (nested && (record(nested.item_layer_types) || Array.isArray(nested.item_layer_types))) return nested;
  const originId = statusOriginDefinitionId(status);
  if (!originId && !statusScalar(status, 'status_origin_id')) return null;
  if (originId === statusItemId(status)) return definition;
  const origin = ctx.read.definition(originId);
  if (!origin) handBack(`${missingReason}:${upper(statusScalar(status, 'status_id'))}:${originId || upper(statusScalar(status, 'status_origin_id'))}`);
  return origin;
}

/** Resolve origin classification before the first tick draw. */
function requireRegenerationOrigins(ctx: JanusCtx, next: Row, removed: ReadonlySet<string>): void {
  for (const status of statusRows(next, 'target')) {
    if (statusPaused(status) || removed.has(upper(status.status_id))) continue;
    const definition = statusDefinition(ctx, status);
    if (upper(ctx.read.value(definition, 'roll_type')) === REGENERATION_ROLL) continue;
    statusTickOrigin(ctx, status, definition);
  }
}

/** Remaining regeneration inputs unavailable to this tick; cleanup uses the common healing routine. */
const ROLL_TYPE_MODIFIERS = ['roll_type_adv', 'roll_type_dis', 'roll_type_against_adv', 'roll_type_against_dis'];
function regenerationStatusGate(ctx: JanusCtx, next: Row, except: string | null, heal: Row | null, source: Row): void {
  if (heal && ctx.read.has(heal, 'status_effect_removed_from_target_after_cast')) handBack('regeneration:heal-removes-after-cast');
  const noHeal = (def: Row): boolean => ctx.read.has(def, 'no_healing_rolls') || ctx.read.has(def, 'status_effect_permanent');
  // ft_creature_no_heal_block_reason scans EVERY applied status — the regenerating one too (Codex A9 r4 P2) — and the regen source itself
  if (noHeal(source) || (heal && noHeal(heal))) handBack('regeneration:no-heal-status');
  for (const other of statusRows(next, 'target')) {
    // a FULL definition (PHP recompiles a layerless shell — Codex A9 r4): the row's own copy only when it carries its layers
    const nested = record(other.status_arr);
    const def = nested && record(nested.item_layer_types) ? nested : ctx.read.definition(statusItemId(other));
    if (!def || !record(def.item_layer_types)) handBack('regeneration:other-status-definition-not-held');
    if (noHeal(def!)) handBack('regeneration:no-heal-status');
    if (except !== null && upper(other.status_id) === except) continue;
    if (modifiesRegenerationRoll(ctx, def!)) handBack('regeneration:roll-type-modifier-status');
  }
}
/** janus_accumulate_roll_type_adv_dis(roll_type_id: the regeneration roll): only a roll_type_adv / _dis / _against_* entry LISTING that roll
 *  type counts (the N5f smoke's Fortunate lists the Skill Roll alias — inert here); an entry the page cannot read counts (a hand-back). */
function modifiesRegenerationRoll(ctx: JanusCtx, row: Row, rollType = REGENERATION_ROLL): boolean {
  for (const k of ROLL_TYPE_MODIFIERS) for (const entry of ctx.read.entries(row, k)) {
    const id = upper(typeof entry === 'string' ? entry.split('|')[0] : itemField(record(entry), 'item_id') ?? record(entry)?.item_id);
    if (!/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(id) || id === rollType) return true;
  }
  return false;
}

/** ft_regeneration_roll_extreme() (2026-10-07): a regeneration tick is rolled by the creature on itself, so both sides of the roll-type
 *  extremes apply — roll_type_is_rolled_at_max / _min [Regeneration Roll] on its row or a held status (casterRollTypeExtreme's read), and
 *  roll_type_against_is_rolled_at_max / _min on a held status (an aura's occupancy status: Master of Triage → max, Sanguine Dominion → min;
 *  target_marked_by_caster only when the creature itself applied it). Max wins. Statuses expiring at this start are not read. */
function regenerationExtreme(ctx: JanusCtx, next: Row, removed: ReadonlySet<string>, rollType = REGENERATION_ROLL): 'max' | 'min' | null {
  const types = new Set([rollType]);
  let max = carrierNamesRollType(ctx, next, 'roll_type_is_rolled_at_max', types);
  let min = carrierNamesRollType(ctx, next, 'roll_type_is_rolled_at_min', types);
  for (const status of statusRows(next, 'target')) {
    if (removed.has(upper(status.status_id))) continue;
    const def = statusDefinition(ctx, status);
    max ||= carrierNamesRollType(ctx, def, 'roll_type_is_rolled_at_max', types);
    min ||= carrierNamesRollType(ctx, def, 'roll_type_is_rolled_at_min', types);
    if (ctx.read.has(def, 'target_marked_by_caster') && upper(status.status_caster_id) !== ecId(next)) continue;
    max ||= carrierNamesRollType(ctx, def, 'roll_type_against_is_rolled_at_max', types);
    min ||= carrierNamesRollType(ctx, def, 'roll_type_against_is_rolled_at_min', types);
  }
  return max ? 'max' : min ? 'min' : null;
}
/** ft_regeneration_roll_dice_arr(): every die forced to its max / min face (tagged Ω / α) when an extreme applies and there are dice, else
 *  the ordinary draw. `marker` is the log tuple the PHP appends after the faces. */
function regenerationDice(ctx: JanusCtx, extreme: 'max' | 'min' | null, dice: number, die: number, adv = 0): { total: number; faces: unknown[]; marker: unknown[] | null } {
  if (!extreme || dice <= 0 || die <= 0) return { ...rollDiceFaces(ctx, dice, die, adv), marker: null };
  const face = extreme === 'max' ? die : 1;
  const tag = extreme === 'max' ? 'Ω' : 'α';
  return { total: dice * face, faces: Array.from({ length: dice }, (_, i) => [face, `d${die}_${i + 1} ${tag}`]),
    marker: extreme === 'max' ? ['Ω', 'Roll Type is Rolled At Max'] : ['α', 'Roll Type is Rolled At Min'] };
}

/** check_status_effect_for_regeneration_and_regenerate_target_arr. */
function statusRegeneration(ctx: JanusCtx, next: Row, removed: ReadonlySet<string>, expiries: readonly StatusRemoval[] = [], effect: TickEffect = (_key, work) => work(), turnLocks = true): void {
  for (const status of statusRows(next, 'target')) {
    if (statusPaused(status)) continue;
    if (removed.has(upper(status.status_id))) { expiryTriggers(ctx, new Set([upper(status.status_id)]), expiries); continue; }
    const definition = statusDefinition(ctx, status);
    effect('regeneration-status:' + upper(status.status_id), () => regenerateStatusRow(ctx, next, status, definition, removed));
    // Target START applies these locks after this row's regeneration, in row order.
    if (turnLocks && int(ctx.read.value(definition, 'no_actions') ?? 0)) ec(next).aps = 0;
    if (turnLocks && int(ctx.read.value(definition, 'no_reactions') ?? 0)) ec(next).reaction = 0;
  }
}

function regenerateStatusRow(ctx: JanusCtx, next: Row, status: Row, definition: Row, removed: ReadonlySet<string>): void {
    const ownRegeneration = upper(ctx.read.value(definition, 'roll_type')) === REGENERATION_ROLL;
    const origin = ownRegeneration ? null : statusTickOrigin(ctx, status, definition);
    const source = ownRegeneration ? definition
      : origin && upper(ctx.read.value(origin, 'roll_type')) === REGENERATION_ROLL ? origin
      : upper(ctx.read.value(definition, 'roll_type')) === TEMP_REGENERATION_ROLL_TYPE ? definition
      : origin && upper(ctx.read.value(origin, 'roll_type')) === TEMP_REGENERATION_ROLL_TYPE ? origin : null;
    if (!source) return;
    const temporary = upper(ctx.read.value(source, 'roll_type')) === TEMP_REGENERATION_ROLL_TYPE;
    const current = requiredInt(ecLean(next, 'v_current'), 'regeneration:vitality-not-held');
    if (Number(ecLean(next, 'encounter_creature_dead')) !== 0 || current <= 0) return;
    if (temporary) {
      // The status's caster supplies formula tags; the target rolls on itself.
      const caster = ctx.members.get(statusCasterId(status)) ?? next;
      const formula = (key: string): number => phpIntval(resolveFormulaVariables(ctx.read.value(source, key) ?? 0, caster, null,
        formulaContract.rollup, { layerValue: (row, name) => ctx.read.value(row as Row | null, name),
          layerEntries: (row, name) => ctx.read.entries(row as Row | null, name), itemField: readItemField,
          computedValue: (row, bucket, name) => ftComputedValue(row, bucket, name) }));
      const dice = formula('dice'), die = formula('die'), mod = formula('mod');
      if (dice <= 0 && die <= 0 && mod === 0) return;
      const axes = ['adv', 'dis', 'against_adv', 'against_dis'] as const;
      const types = new Set([TEMP_REGENERATION_ROLL_TYPE]);
      const sourceStacks = Number(carrierNamesRollType(ctx, source, 'roll_type_adv', types)) - Number(carrierNamesRollType(ctx, source, 'roll_type_dis', types));
      const adv = Math.max(-5, Math.min(5, sourceStacks + rollTypeModifierStacks(ctx, next, [TEMP_REGENERATION_ROLL_TYPE], axes,
        { usedItemType: String(itemField(source, 'item_type') ?? ''), usedRollType: TEMP_REGENERATION_ROLL_TYPE, rollTo: ecId(next), opposing: next })));
      const rolled = regenerationDice(ctx, regenerationExtreme(ctx, next, removed, TEMP_REGENERATION_ROLL_TYPE), dice, die, adv);
      const result = rolled.total + mod;
      if (grantTemporaryVitality(next, result)) insertRoll(ctx, { roll_type_1: TEMP_REGENERATION_ROLL_TYPE,
        roll_item_id: statusItemId(status), by: next, to: next, roll_result: result,
        rolls_full: [...(rolled.faces.length || mod ? [[mod, 'sum_mod'], ...rolled.faces] : []), ...(rolled.marker ? [rolled.marker] : [])], dice, die, mod, adv_dis_stacks: adv });
      return;
    }
    if (ctx.read.entries(source, 'no_regeneration_if_damage_type_taken').length)
      handBack('regeneration:damage-type-blocked'); // PHP reads the rolls table since the last end_turn
    const maximum = requiredInt(record(record(next.computed)?.vitality)?.effective_v_max, 'regeneration:vitality-not-held');
    if (requiredInt(record(next.encounter_creatures)?.encounter_creature_dead ?? 0, 'regeneration:vitality-not-held') !== 0
        || current <= 0 || current >= maximum) return;
    regenerationStatusGate(ctx, next, upper(status.status_id), definition, source);
    const dice = requiredInt(ctx.read.value(source, 'dice') ?? 0, 'regeneration:dice');
    const die = requiredInt(ctx.read.value(source, 'die') ?? 0, 'regeneration:dice');
    const mod = requiredInt(ctx.read.value(source, 'mod') ?? 0, 'regeneration:dice');
    if (dice <= 0 && die <= 0 && mod === 0) return;
    if (modifiesRegenerationRoll(ctx, source) || modifiesRegenerationRoll(ctx, next)) handBack('regeneration:roll-type-modifier');
    const rolled = regenerationDice(ctx, regenerationExtreme(ctx, next, removed), dice, die);
    const result = rolled.total + mod;
    ec(next).v_current = Math.min(maximum, current + Math.max(0, result));
    healingStatusChanges(ctx, { casterId: ecId(next), targetIds: [ecId(next)], abilityId: statusItemId(status), rawDiceMod: 0,
      rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null }, definition, next);
    // check_status_effect_…'s log drops only roll_dice_arr's roll_result row: [sum_mod, faces…] (just [sum_mod] for a modifier-only tick).
    const statusFaces: unknown[] = rolled.faces.length ? [[mod, 'sum_mod'], ...rolled.faces] : mod ? [[mod, 'sum_mod']] : [];
    if (rolled.marker) statusFaces.push(rolled.marker);
    insertRoll(ctx, { roll_type_1: REGENERATION_ROLL, roll_item_id: statusItemId(status), by: next, to: next,
      roll_result: result, rolls_full: statusFaces, dice, die, mod });
}

/** check_ability_for_regeneration_and_regenerate_target_arr: per-entry start-turn upkeep. */
function abilityRegeneration(ctx: JanusCtx, next: Row, entries: TurnTickEntry[], removed: ReadonlySet<string> = new Set()): void {
  for (const entry of entries) {
    const source = entry.definition;
    if (!source) handBack('regeneration:ability-definition-not-held');
    if (upper(ctx.read.value(source, 'roll_type')) !== REGENERATION_ROLL) continue;
    // R-141 (2026-10-07): only an always-on, currently-active passive ticks from its bucket (ft_regeneration_bucket_entry_ticks twin)
    // R-197: the light gate reads the tick's CURRENT members (status changes earlier in this pass move light sources); an inventory change
    // this pass (a moved / destroyed light-source object) is not patched → no reader → the gate hands back
    const light = ctx.read.lightTypesPresent;
    const gateRead = { ...ctx.read, lightTypesPresent: light && !ctx.inventoryChanged.length ? (holder: Row) => light(holder, [...ctx.members.values()]) : undefined };
    const ticks = regenerationEntryTicks(gateRead, next, entry, source, removed);
    if (ticks === 'unknown') handBack('regeneration:passive-conditions');
    if (!ticks) continue;
    // The SQL damage-history query in PHP is authoritative for blocked types;
    // neither the encounter blob nor the tick reader holds those prior rolls.
    if (ctx.read.entries(source, 'no_regeneration_if_damage_type_taken').length)
      handBack('regeneration:damage-type-blocked');
    if (['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target'].every(k => ctx.read.has(source, k))) continue;
    const current = requiredInt(record(next.encounter_creatures)?.v_current ?? 0, 'regeneration:vitality-not-held');
    const maximum = requiredInt(record(record(next.computed)?.vitality)?.v_max, 'regeneration:vitality-not-held');
    if (requiredInt(record(next.encounter_creatures)?.encounter_creature_dead ?? 0, 'regeneration:vitality-not-held') !== 0
        || current <= 0 || current >= maximum) continue;
    regenerationStatusGate(ctx, next, null, source, source);
    const dice = requiredInt(ctx.read.value(source, 'dice') ?? 0, 'regeneration:dice');
    const die = requiredInt(ctx.read.value(source, 'die') ?? 0, 'regeneration:dice');
    const mod = requiredInt(ctx.read.value(source, 'mod') ?? 0, 'regeneration:dice');
    if (modifiesRegenerationRoll(ctx, source) || modifiesRegenerationRoll(ctx, next)) handBack('regeneration:roll-type-modifier');
    const rolled = regenerationDice(ctx, regenerationExtreme(ctx, next, removed), dice, die);
    const result = rolled.total + mod;
    ec(next).v_current = Math.min(maximum, current + Math.max(0, result));
    healingStatusChanges(ctx, { casterId: ecId(next), targetIds: [ecId(next)], abilityId: entry.id, rawDiceMod: 0,
      rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null }, source, next);
    insertRoll(ctx, { roll_type_1: REGENERATION_ROLL, roll_item_id: entry.id, by: next, to: next,
      roll_result: result, rolls_full: rolled.marker ? [...rolled.faces, rolled.marker] : rolled.faces, dice, die, mod });
  }
}

function startAbilityCharges(ctx: JanusCtx, next: Row, entries: TurnTickEntry[], removed: ReadonlySet<string> = new Set(), effect: TickEffect = (_key, work) => work()): void {
  const concentrating = statusRows(next, 'target').some(s => statusItemId(s) === CONCENTRATING);
  for (const entry of entries) {
    if (entry.type === 'lair_ability') continue;
    effect('start-ability:' + entry.id, () => {
    const definition = entry.definition;
    if (!definition) handBack('turn-tick:ability-definition-not-held');
    const needsCharges = ctx.read.has(definition, 'recharge_range_end')
      || ctx.read.has(definition, 'ability_charge_decay_at_start_of_turn') || entry.id === PSI;
    if (needsCharges && (entry.charges === null || entry.max === null)) handBack('turn-tick:charge-state-not-held');
    const chargeState = needsCharges
      ? abilityChargeEntry(ctx, next, entry.id) : null;
    // PHP tests all three branches against its local caster_ability copy. The
    // subsequent adjust_charges() calls mutate caster_arr, not that local copy.
    const hasRecharge = ctx.read.has(definition, 'recharge_range_end') && chargeState !== null && chargeState.current < chargeState.max;
    const hasDecay = ctx.read.has(definition, 'ability_charge_decay_at_start_of_turn') && (chargeState?.current ?? 0) > 0;
    const hasPsi = entry.id === PSI && !concentrating && chargeState !== null && chargeState.current < chargeState.max;
    if (hasRecharge) {
      const begin = requiredInt(ctx.read.value(definition, 'recharge_range_begin') ?? 0, 'turn-tick:recharge-begin');
      const die = requiredInt(ctx.read.value(definition, 'recharge_range_end'), 'turn-tick:recharge-die');
      if (begin <= 1) adjustCharges(ctx, next, entry.id, 1, true);
      else {
        // The recharge item's against_* layers describe incoming rolls against
        // its owner; janus_roll_type_adv_dis_item_delta reads only its own adv/dis.
        const rechargeDelta = (['roll_type_adv', 'roll_type_dis'] as const).reduce((sum, key) =>
          sum + (ctx.read.entries(definition, key).some(v => upper(itemField(record(v), 'item_id') ?? v) === TURN_RECHARGE_ROLL)
            ? (key === 'roll_type_adv' ? 1 : -1) : 0), 0);
        if (['roll_type_adv', 'roll_type_dis', 'roll_type_against_adv', 'roll_type_against_dis'].some(k =>
          ctx.read.has(next, k))) handBack('turn-tick:recharge-adv-dis');
        const sources = [...statusRows(next, 'target').map(s => statusDefinition(ctx, s)),
          ...entries.filter(e => e.type === 'passive_ability' && !e.inactive).map(e => e.definition).filter((d): d is Row => !!d)];
        if (sources.some(source => ['roll_type_adv', 'roll_type_dis', 'roll_type_against_adv', 'roll_type_against_dis',
          'rolls_adv', 'rolls_dis'].some(k => ctx.read.has(source, k)))) handBack('turn-tick:recharge-adv-dis-source');
        const rolled = rollDiceFaces(ctx, 1, die, rechargeDelta);
        insertRoll(ctx, { roll_type_1: TURN_RECHARGE_ROLL, roll_item_id: entry.id, by: next, to: next,
          roll_result: rolled.total, rolls_full: rolled.faces, dice: 1, die, adv_dis_stacks: rechargeDelta, dc: begin });
        if (rolled.total >= begin) adjustCharges(ctx, next, entry.id, 1, true);
      }
    }
    if (hasDecay) {
      if (entry.id === FURY && ctx.read.has(next, 'controlled_fury')) return;
      adjustCharges(ctx, next, entry.id, -1, true);
    }
    if (hasPsi) adjustCharges(ctx, next, entry.id, 1, true);
    abilityRegeneration(ctx, next, [entry], removed);
    });
  }
}

/** The universal Fortunate at a turn start (janus_trigger_inject_universal_fortunate() makes this reaction available even when it is absent
 *  from the compiled ability buckets; dispatched through the same janus() port as a player's use). Reads the row's CURRENT luck / reaction —
 *  the stored reaction before the refresh, the reset one after it. True when the conditions held (asked, fired or declined). */
function turnStartFortunate(ctx: JanusCtx, next: Row, trigger: string): boolean {
  if (!passesDbGate(ctx.read, FORTUNATE, next, null)) return false;
  const lucky = requiredInt(record(next.encounter_creatures)?.luck ?? 0, 'turn-tick:luck-not-held');
  const reaction = requiredInt(record(next.encounter_creatures)?.reaction ?? 0, 'turn-tick:reaction-not-held');
  if (lucky <= 0 || reaction <= 0 || statusRows(next, 'target').some(s => statusItemId(s) === FORTUNATE)
    || ctx.statusesAdded.some(s => upper(s.status_item_id) === FORTUNATE && upper(s.target) === ecId(next))) return false;
  const ability = ctx.read.definition(FORTUNATE);
  if (!ability) handBack('turn-tick:fortunate-definition-not-held');
  // OWNER RULING 2026-10-02 (ability/trigger-consent.ts): the universal Fortunate is a REACTION — a player's creature spends its luck on it only
  // on the controller's yes (a decline / the 5 s silence: nothing fires, nothing is spent)
  if (consentAllows(ctx, next, ability, FORTUNATE, trigger, 'reaction_ability'))
    janus(ctx, { casterId: ecId(next), targetIds: [ecId(next)], abilityId: FORTUNATE,
      rawDiceMod: 0, rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null }, ability, next, [next]);
  return true;
}

/** OWNER RULING 2026-10-03 (Fortunate, memory `feedback_trigger_prompt_5s`): a PLAYER-controlled creature is prompted ONCE per turn start —
 *  "right before their turn starts … if they have a reaction (to min-max it a bit). If not, after they regain their reaction and AP". So when
 *  the stored reaction is still held, the prompt comes BEFORE the refresh (a yes spends that old reaction, then the reset restores it); its
 *  answer, yes or no, is the turn start's one prompt. An NPC / GM creature and a reader-less host keep PHP's single post-refresh auto-fire.
 *  WORKER PICK (owner to review): "has a reaction" reads the stored reaction column exactly as PHP's universal Fortunate does at a luck gain
 *  (no no_reactions status re-check). Returns true when this was the turn start's Fortunate (the post-refresh pass then skips it). */
function fortunateBeforeRefresh(ctx: JanusCtx, next: Row, removed: ReadonlySet<string>): boolean {
  // a controller the page cannot name ('?') keeps PHP's timing (the post-refresh pass, pre-asked through the relay)
  if (!isPlayerController(ctx, next)) return false;
  if (!passesDbGate(ctx.read, FORTUNATE, next, null)
    || requiredInt(record(next.encounter_creatures)?.luck ?? 0, 'turn-tick:luck-not-held') <= 0
    || requiredInt(record(next.encounter_creatures)?.reaction ?? 0, 'turn-tick:reaction-not-held') <= 0) return false;
  // Native Fortunate can write its inputs. Isolate only the fields it touches;
  // both status views exclude START expiries, while resource writes use the working row.
  const view = frameCopyOnRead(next, new Map(), { catalogs: 'share' }).encounter;
  for (const id of removed) removeStatusFromRow(view, id);
  view.encounter_creatures = next.encounter_creatures;
  return turnStartFortunate(ctx, view, 'before_turn_start_refresh');
}

/** WORKER PICK (owner to review) — the FIRST turn of a combat (START MODE): there is no previous round whose reaction could be min-maxed (the
 *  combat start's own reset is the refresh), and the start page cannot wait on a prompt between its synthetic passes and the initiative's real
 *  dice — so a known player's first-turn Fortunate is OFFERED after the start commits ("after they regain their reaction and AP at the start of
 *  their turn … they are prompted"), exactly like a luck gain. True when deferred. */
function deferStartModeFortunate(ctx: JanusCtx, next: Row, startMode: boolean): boolean {
  if (!startMode || !isPlayerController(ctx, next)) return false;
  const lucky = requiredInt(record(next.encounter_creatures)?.luck ?? 0, 'turn-tick:luck-not-held');
  const reaction = requiredInt(record(next.encounter_creatures)?.reaction ?? 0, 'turn-tick:reaction-not-held');
  if (lucky > 0 && reaction > 0 && passesDbGate(ctx.read, FORTUNATE, next, null)
      && !statusRows(next, 'target').some(s => statusItemId(s) === FORTUNATE)) (ctx.fortunateOffers ??= new Set()).add(ecId(next));
  return true;
}

function turnBeginTriggers(ctx: JanusCtx, next: Row): void {
  const event = { trigger: 'triggers_when_caster_turn_begins', excludeAbilityIds: [FORTUNATE] };
  triggerFamilyPreDraw(ctx, next, event);
  runTriggerFamily(ctx, event, next, null, [next], false, undefined, true);
}

/** Explore unavailable effects on disposable clones before drawing; skip each owed effect
 * independently, then run supported hooks once through the native turn commit. */
/** Extreme-dice branches: all max, all min, and max/min alternating in runs of 1, 2 and 3 draws (either phase) — a contest's
 * skill die and an ADV/DIS second d20 shift which draw belongs to which side, so a period-1 alternation alone misses outcomes
 * (Codex review 2026-09-27: Spiral of Light reached push_ft_after_hit after five real draws). */
const DRY_RUN_PATTERNS = ['max', 'min', 'alt1-hi', 'alt1-lo', 'alt2-hi', 'alt2-lo', 'alt3-hi', 'alt3-lo'] as const;
const DRY_RUN_RESIDUALS = new Set(['caster_gains_luck:second-gain-this-use']);
/** The high / low choice `pattern` makes at its draw `n` (0-based). */
function patternHigh(pattern: (typeof DRY_RUN_PATTERNS)[number], n: number): boolean {
  if (pattern === 'max' || pattern === 'min') return pattern === 'max';
  const firstRun = Math.floor(n / Number(pattern[3])) % 2 === 0;
  return pattern.endsWith('-hi') ? firstRun : !firstRun;
}
/** `choices` (optional) records the high / low choice of every draw the run consumes. */
function syntheticRollSource(pattern: (typeof DRY_RUN_PATTERNS)[number], choices?: boolean[]): RollSource {
  let n = 0;
  const high = (): boolean => { const h = patternHigh(pattern, n); choices?.push(h); return h; };
  return {
    d(die: number): number { const face = high() ? die : 1; n += 1; return face; },
    fraction(): number { const value = high() ? 0.999999 : 0; n += 1; return value; },
  };
}

/** The complete compiled status projection used by local tick hooks and received/provisional browser frames. */
export function tickStatusProjection(s: StatusRow, statusId: string): Row {
  const row: Row = { ...s, status_id: statusId, status_item_id: upper(s.status_item_id), status_target_id: upper(s.target),
    status_caster_id: upper(s.caster) };
  const ends = String(s.ends ?? '').split('-').map(Number);
  ['year', 'month', 'day', 'hour', 'minute', 'second'].forEach((unit, k) => { row[`status_ends_campaign_${unit}`] = Number.isFinite(ends[k]) ? ends[k] : 0; });
  return row;
}

/** Installs a delivered status only in the buckets this member owns. */
export function upsertTickStatusProjection(row: Row, entry: Row): void {
  const sid = upper(entry.status_id); if (!sid) return;
  const statuses = (row.statuses ??= {}) as Row, me = ecId(row);
  const target = upper(entry.status_target_id), caster = upper(entry.status_caster_id) || target;
  const same = (s: Row): boolean => upper(s.status_id) === sid || (!!s.tick_added && statusItemId(s) === statusItemId(entry)
    && upper(s.status_target_id) === target && upper(s.status_caster_id) === caster);
  for (const mode of ['target', 'caster'] as const) {
    if ((mode === 'target' ? target : caster) !== me) continue;
    const list = Array.isArray(statuses[mode]) ? statuses[mode] as Row[] : [];
    const i = list.findIndex(same);
    if (i >= 0) list[i] = { ...list[i], ...entry }; else list.unshift({ ...entry });
    statuses[mode] = list;
  }
}

export function projectStatuses(ctx: JanusCtx, row: Row): Row {
  const id = ecId(row);
  const statuses = record(row.statuses) ?? {};
  const removed = (s: Row): boolean => ctx.statusesRemoved.some(r => upper(r.target) === upper(s.status_target_id)
    && (r.status_id ? upper(r.status_id) === upper(s.status_id) : upper(r.status_item_id) === statusItemId(s)));
  const target = statusRows(row, 'target').filter(s => !s.tick_added && !removed(s));
  const caster = statusRows(row, 'caster').filter(s => !s.tick_added
    && !removed(s));
  ctx.statusesAdded.forEach((s, i) => {
    if (upper(s.target) !== id && upper(s.caster) !== id) return;
    const row: Row = { ...tickStatusProjection(s, `TICK-PENDING-${i}`), tick_added: true };
    if (upper(s.target) === id) target.push(row);
    if (upper(s.caster) === id) caster.push({ ...row });
  });
  return { ...row, statuses: { ...statuses, target, caster } };
}

/** Decide unported effects on synthetic clones, then run supported phases once with real dice. */
export function resolveEndTurnTicks(input: TurnTickInput): TurnTickResult {
  const shared = input.prepare || input.prepareEnd ? undefined : input.preflight;
  const skipped = new Map<string, string>(shared?.skipped ?? []);
  // Fresh mutable state per outcome; catalog definitions survive by reference and writers replace their maps.
  const dryEncounter = (): Row => frameCopyOnRead(input.encounter, new Map(), { catalogs: 'share' }).encounter;
  const cloneRow = (row: Row): Row => frameCopyOnRead(row, new Map(), { catalogs: 'share' }).encounter;
  for (;;) {
    try {
      // DRY-RUN DEDUPE (fix-bug 2026-10-14, ability-use.ts dryRunTwin): a run is a pure function of the inputs, the faces it draws and the
      // picks it is forced, so a pattern making the same high / low choice at every draw a finished run with the same forced picks consumed
      // replays it exactly — skipped, its recorded pick sizes still branching the tree. Per attempt: a TickEffectSkip changes `skipped`.
      const done: Array<{ forced: string; choices: boolean[]; sizes: number[] }> = [];
      if (!shared?.skipped) for (const pattern of DRY_RUN_PATTERNS) {
        // Explore the partial Fisher-Yates pick tree, including later picks whose pool an earlier fire changed.
        // Channel random picks use the same explorer: a middle slug may force-evoke a different payload.
        const pending: number[][] = [[]];
        let runs = 0;
        while (pending.length) {
          if (++runs > 4096) handBack('preflight:turn-tick:random-pick-space-too-large');
          const forced = pending.pop()!;
          const forcedKey = forced.join(',');
          const twin = done.find(run => run.forced === forcedKey && run.choices.every((high, n) => patternHigh(pattern, n) === high));
          const sizes: number[] = twin ? twin.sizes : [];
          if (!twin) {
            const choices: boolean[] = [];
            try { runTicks(input, dryEncounter(), syntheticRollSource(pattern, choices), size => {
              const index = sizes.length; sizes.push(size);
              return forced[index] ?? 1;
            }, skipped, cloneRow); }
            catch (error) {
              if (error instanceof TickEffectSkip) throw error;
              if (error instanceof HandBack && !DRY_RUN_RESIDUALS.has(error.reason)) handBack(`preflight:${error.reason}`);
              if (!(error instanceof HandBack) && !(error instanceof Refusal))
                handBack(`preflight:exception:${String((error as Error)?.message ?? error).slice(0, 80)}`);
            }
            done.push({ forced: forcedKey, choices, sizes });
          }
          // Each prefix is visited once; branch at the first newly encountered pick and discover the rest on that replay.
          if (sizes.length > forced.length) for (let face = sizes[forced.length]!; face >= 1; face--)
            pending.push([...forced, face]);
        }
      }
      // OWNER RULING 2026-10-02: the dry runs met an optional trigger (a reaction) of a player-controlled creature nobody has answered → the page
      // asks its controller (≤ 5 s) and resolves again with the answer; never a real die first
      if (consentPending(input.read.ability)) handBack(`preflight:${TRIGGER_CONSENT_REASON}`);
      if (shared && !shared.skipped) shared.skipped = new Map(skipped);
      const result = runTicks(input, dryEncounter(), input.preflightOnly ? syntheticRollSource('min') : input.read.roll, undefined, skipped, cloneRow);
      if (!input.preflightOnly) result.rows.push(...[...new Map([...skipped].map(([effect, reason]) => [reason, effect]))].map(([reason, effect]) => ({
        ...skipUnported(reason, { boundary: 'turn-tick', actorId: input.endingId, nextId: input.nextId }),
        roll_by: effect.startsWith('lair-refresh:') || effect.startsWith('end-') || effect === 'orb-end' ? input.endingId : input.nextId,
      })));
      return result;
    } catch (error) {
      if (!(error instanceof TickEffectSkip)) throw error;
      skipped.set(error.effect, error.reason);
    }
  }
}

/** START MODE (lane A8 for the combat lane, 2026-10-02 — R-130's first-turn half): ft_combat_start_first_turn_legacy_hooks() runs, for the FIRST
 *  combatant only, the start half of a turn boundary — reset_encounter_creature_start_of_turn_variables (the caller's resolveStartOfTurn), the
 *  start_turn log, check_ability_types_for_next_encounter_creature_id (recharge / decay / psi), the status passes (regeneration) and the
 *  mandatory orb channels and turn-begin carriers — with NO ending actor and NO end_turn row, using the same pre-draw dry runs. */
export function resolveStartTurnTicks(input: Omit<TurnTickInput, 'endingId' | 'endRemoved'>): TurnTickResult {
  return resolveEndTurnTicks({ ...input, endingId: null, endRemoved: new Set() });
}

/** The turn frame saves these plain expiries itself; retire projections and decide loss listeners at the same phase. */
const consumedExpiries = new WeakMap<JanusCtx, Set<string>>();
function expiryTriggers(ctx: JanusCtx, ids: ReadonlySet<string>, expiries: readonly StatusRemoval[] = []): void {
  // Independent connections retain their legacy hook order; each linked closure is atomic.
  if (ids.size > 1) { for (const id of ids) expiryTriggers(ctx, new Set([id]), expiries); return; }
  const consumed = consumedExpiries.get(ctx) ?? new Set<string>(); consumedExpiries.set(ctx, consumed);
  const work = new Set(ids);
  const held = [...ctx.members.values()].flatMap(member => statusRows(member, 'target'));
  const planned = new Set(expiries.map(r => upper(r.status_id)));
  let linked = false;
  for (const id of ids) {
    const selected = held.find(s => upper(scalar(s.status_id)) === upper(id));
    const connection = upper(scalar(selected?.status_connected_id));
    if (!connection) continue;
    const peers = held.filter(s => upper(scalar(s.status_connected_id)) === connection);
    if (peers.some(s => LINK_STATUS_IDS.has(statusItemId(s)))) {
      linked = true;
      for (const peer of peers) if (planned.has(upper(scalar(peer.status_id)))) work.add(upper(scalar(peer.status_id)));
    }
  }
  const batch: Array<{ id: string; owner: Row; status: Row; expiry?: StatusRemoval }> = [];
  for (const id of work) {
    if (consumed.has(upper(id))) continue;
    let owner: Row | undefined, status: Row | undefined;
    for (const member of ctx.members.values()) {
      const found = statusRows(member, 'target').find(s => upper(s.status_id) === upper(id));
      if (found) { owner = member; status = found; break; }
    }
    const expiry = expiries.find(e => upper(e.status_id) === upper(id));
    if (!owner || !status) {
      const inventory = [...heldInventoryRows(ctx).values()].find(row => statusRows(row, 'target').some(s => upper(s.status_id) === upper(id)));
      if (inventory && expiry) {
        // A held item is not a departed creature. Its turn frame saves the exact removal;
        // retire only this item's borrowed decision branches before later hooks read it.
        applyStatusFrame(inventory, { status_remove_ids: [upper(id)], skills: {} }, ctx.read.definition);
        for (const member of ctx.members.values()) removeStatusFromRow(member, id);
        consumed.add(upper(id));
        insertRoll(ctx, { roll_type_1: expiry.log_key, roll_item_id: expiry.item_id, by: ctx.members.get(expiry.caster_id)! });
        continue;
      }
      const source = [...ctx.members.values()].flatMap(member => statusRows(member, 'caster'))
        .find(s => upper(statusInputSnapshot(s).status_id) === upper(id));
      const before = source && statusRemovalBefore(source);
      if (!expiry || !before || ctx.members.has(upper(before.status_target_id))) handBack('turn-tick:expiry-row-not-held');
      // Only the caster projection survives. Persist its exact row identity through
      // the shared tombstone lane; no holder-side hooks or compiled effects remain.
      delete before.status_caster_id; // The shared row contract pins target/item/connection for tombstones.
      ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, { version: 'janus.spawn.rows.v1', writes: [{ table: 'statuses', op: 'update',
        row: { status_id: upper(id), status_deleted: 1 }, before }] });
      for (const member of ctx.members.values()) removeStatusFromRow(member, id);
      consumed.add(upper(id));
      insertRoll(ctx, { roll_type_1: expiry.log_key, roll_item_id: expiry.item_id, by: ctx.members.get(expiry.caster_id)! });
      continue;
    }
    batch.push({ id: upper(id), owner, status, expiry });
  }
  if (linked) {
    const projections = new Map([...new Set(batch.map(r => r.owner))].map(owner => {
      const runtime = statusRemovalRuntime(owner, batch.filter(r => r.owner === owner).map(r => r.id), ctx.read.definition);
      if (!runtime.ok) handBack('status-compiled-footprint:' + runtime.layer);
      return [owner, runtime] as const;
    }));
    // The source helper owns concentration retirement; the surrounding turn frame owns its save.
    // Complete every physical/projection removal before any member's loss listeners observe the group.
    for (const { id, owner, status } of batch) if (statusItemId(status) === CONCENTRATING) {
      const writes = ctx.statusesRemoved.length;
      removeLoneConcentration(ctx, ecId(owner), id);
      ctx.statusesRemoved.splice(writes);
    }
    for (const owner of new Set(batch.map(r => r.owner))) {
      const entries = batch.filter(r => r.owner === owner);
      const frame = statusRemovalFrame(owner, entries.flatMap(r => r.expiry ? [r.expiry] : []));
      applyStatusFrame(owner, { ...frame, status_remove_ids: entries.map(r => r.id) }, ctx.read.definition);
      applyStatusRemovalRuntime(owner, projections.get(owner)!);
    }
    for (const { id } of batch) {
      for (const member of ctx.members.values()) removeStatusFromRow(member, id);
      consumed.add(id);
    }
  }
  for (const { id, owner, status, expiry } of batch) {
    if (!linked) {
      applyStatusFrame(owner, { status_remove_ids: [id], skills: skillsWithoutStatuses(owner, [id]).skills,
        ...(expiry?.defenses !== undefined ? { defenses: expiry.defenses } : {}) }, ctx.read.definition);
      for (const member of ctx.members.values()) removeStatusFromRow(member, id);
      consumed.add(id);
    }
    const event = { trigger: 'triggers_when_caster_loses_status_effect', statusItemId: statusItemId(status) };
    triggerFamilyPreDraw(ctx, owner, event);
    runTriggerFamily(ctx, event, owner, null, [owner], false, undefined, true);
    if (expiry) insertRoll(ctx, { roll_type_1: expiry.log_key, roll_item_id: expiry.item_id, by: ctx.members.get(upper(expiry.log_by))! });
  }
}

function runTicks(input: TurnTickInput, encounter: Row, roll: RollSource, randomPickProbe?: JanusCtx['randomPickProbe'], skipped = new Map<string, string>(),
  cloneRow: (row: Row) => Row = clone): TurnTickResult {
  const originals = new Map(input.roster.map(row => [ecId(row), row]));
  const members = new Map(input.roster.map(row => [ecId(row), cloneRow(row)]));
  // The tick hooks use the projected row, while ability handlers combine stored rows with statusesAdded themselves.
  // Hide only the projection copies from those handlers so modifiers and stack detection see each pending status once.
  const read = { ...input.read.ability, statuses: (row: Row) => input.read.ability.statuses(row).filter(s => !s.tick_added) };
  const ctx: JanusCtx = { encounter, members, originals, read, roll,
    rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [], statusConnectedId: null, rollPrivate: 0, randomPickProbe,
    // Combat-start's start-only aggregate has a separate saver; retain its due-choice skip until that transport owns an outbox.
    ...(input.endingId !== null ? { teleportOffers: [] } : {}),
    dryRun: randomPickProbe !== undefined || input.preflightOnly === true, masteryMarkers: true, statusStacks: [], ...(read.spawnSummons ? { summons: [] } : {}) };
  const changed: TurnTickResult['statusesChanged'] = [];
  const stackChanges = (): TurnTickResult['statusesChanged'] => [...new Map([...changed, ...(ctx.statusStacks ?? [])]
    .map(s => [s.status_id, { status_id: s.status_id, status_stack: s.status_stack }])).values()];
  const radiusChanges = input.endingId !== null && !input.orbOnly ? input.zones?.radiusChanges ?? [] : [];
  const radiusDelta = radiusChanges.length ? { radiusChanges } : {};
  applyZoneRadiusChanges(members.values(), radiusChanges);
  let endPhase = true;
  const effect = <T>(key: string, work: () => T): T | undefined => {
    if (skipped.has(key)) return undefined;
    try {
      const answer = work();
      if (ctx.summons?.length && ctx.read.spawnSummons) {
        ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, ctx.read.spawnSummons(ctx, ctx.summons)); ctx.summons = [];
      }
      const partial: TurnTickResult = { rows: ctx.rows, ec: ecChanges(ctx), statusesAdded: ctx.statusesAdded,
        statusesRemoved: ctx.statusesRemoved, statusesChanged: stackChanges(), charges: ctx.chargesChanged, ...radiusDelta,
        ...(ctx.teleportOffers?.length ? { teleportOffers: ctx.teleportOffers } : {}),
        ...(spawnPlanHasEffects(ctx.spawnRows) ? { spawnRows: ctx.spawnRows } : {}) };
      // Attribute unavailable transport/bindings to the effect that introduced
      // them on synthetic branches; independent hooks survive the restart.
      const pendingMarkers = (ending ? 1 : 0) + 1 - ctx.rows.filter(row => row.roll_type_1 === 'end_turn' || row.roll_type_1 === 'start_turn').length;
      if (ctx.rows.length + new Set(skipped.values()).size + pendingMarkers > 64 || ctx.statusesAdded.length > 8 || ctx.statusesRemoved.length > 8
        || changed.length > 8 || ctx.chargesChanged.length > 16 || Object.values(partial.ec).some(columns => Object.keys(columns).length > 12))
        handBack('turn-tick:commit-capacity');
      for (const removal of ctx.statusesRemoved) {
        // Turn transport alone needs the full stored input. Shared ability/GM
        // removals keep their own smaller wire shape, including nested hooks.
        if (removal.status_id) {
          const original = originals.get(upper(removal.target));
          const status = original && statusRows(original, 'target').find(s => upper(s.status_id) === upper(removal.status_id));
          if (!status) handBack('turn-tick:exact-removal-input-not-held');
          removal.caster = statusCasterId(status);
          removal.before = statusInputSnapshot(status);
        }
        if (removal.status_id && (!/^[0-9A-F]{8}(-[0-9A-F]{4}){3}-[0-9A-F]{12}$/i.test(removal.caster ?? '') || !record(removal.before)))
          handBack('turn-tick:exact-removal-input-not-held');
      }
      assertTurnTickContract(partial, 64);
      if (endPhase && input.endingId !== null) input.prepareEnd?.(partial);
      input.prepare?.(partial);
      return answer;
    }
    catch (error) {
      // Optional player reactions still prompt before drawing.
      if (!(error instanceof HandBack) || error.reason.includes(TRIGGER_CONSENT_REASON)) throw error;
      if (!ctx.dryRun) throw error; // Never replay real dice. The outer boundary skips a residual tick plan.
      throw new TickEffectSkip(key, error.reason);
    }
  };
  const ending = input.endingId === null ? null : members.get(upper(input.endingId));
  let next: Row = members.get(upper(input.nextId)) ?? handBack('turn-tick:member-not-held');
  if (input.endingId !== null && !ending) handBack('turn-tick:member-not-held');
  if (input.outOfCombat && ending) effect('dying-stratum', () => runDyingStratum(ctx, ending));
  if (input.orbOnly) {
    try {
      if (input.orbOnly === 'pass') effect('orb-end', () => orbEndOfTurnPass(ctx, next));
      else effect('orb-start', () => orbStartOfTurnChannelPass(ctx, next));
    } catch (error) {
      if (error instanceof HandBack && /(?:creature|pc)-death-not-modeled|lethal-no-death-tail/.test(error.reason))
        handBack('turn-tick:orb-pulse-may-kill');
      throw error;
    }
    if (ctx.inventoryChanged.length || ctx.turnTransition || ctx.turnDeathNone) handBack('turn-tick:orb-pass-follow-up-not-modeled');
    if (ctx.rows.length + new Set(skipped.values()).size > 64 || ctx.statusesAdded.length > 8 || ctx.statusesRemoved.length > 8 || ctx.chargesChanged.length > 16)
      handBack('turn-tick:commit-capacity');
    const result: TurnTickResult = { rows: ctx.rows, ec: ecChanges(ctx), statusesAdded: ctx.statusesAdded,
      statusesRemoved: ctx.statusesRemoved, statusesChanged: [], charges: ctx.chargesChanged,
      ...(ctx.teleportOffers?.length ? { teleportOffers: ctx.teleportOffers } : {}),
      ...(spawnPlanHasEffects(ctx.spawnRows) ? { spawnRows: ctx.spawnRows } : {}),
      ...(ctx.statusTriggerListeners?.length ? { statusTriggerListeners: ctx.statusTriggerListeners } : {}),
      ...(ctx.fortunateOffers?.size ? { fortunateOffers: [...ctx.fortunateOffers] } : {}) };
    if (ctx.statusStacks?.length || ctx.playerCharactersChanged?.length) handBack('turn-tick:orb-pass-stack-or-sheet-write');
    assertTurnTickContract(result, 64);
    input.prepare?.(result);
    return result;
  }
  // Resolve missing origin classification before any DoT, contest or recharge draw.
  if (!input.skipStart) effect('regeneration-origins', () => requireRegenerationOrigins(ctx, next, input.startRemoved));
  if (ending && !Number(ecLean(ending, 'encounter_creature_dead'))) {
    // end_turn_inc.php reconciles stale zone grants before the status hooks;
    // check_persistent_aoe_end_of_turn dispatches contacts after those hooks.
    effect('end-expiry-triggers', () => expiryTriggers(ctx, new Set(statusRows(ending, 'caster').map(s => upper(s.status_id)).filter(id => input.endRemoved.has(id))), input.endExpiries));
    effect('zone-exits', () => reconcileEndOfTurnZoneExits(ctx, ending, input.zones));
    effect('end-maintained-upkeep', () => { runMaintainedExpiry(ctx, ending, 'end'); runMaintainedUpkeep(ctx, ending); });
    effect('end-status-ticks', () => endTargetTicks(ctx, ending, input.endRemoved, changed, input.endExpiries, effect, input.zones));
    effect('zone-dispatches', () => runEndOfTurnZones(ctx, ending, input.zones));
    effect('end-mastery-upkeep', () => runMasteryUpkeep(ctx, ending));
    // end_turn_inc.php: the status passes, the zone / wall / Mastery hooks (gates), then the MACHINIST ORB pulse pass (ft_orb_end_of_turn_pass:
    // each channeled orb fires its passive front to back — lane A10, R-026 plan F2; was the `orb-queue` hand-back), the lair reset, end_turn.
    try { if (!input.outOfCombat) effect('orb-end', () => orbEndOfTurnPass(ctx, ending)); }
    catch (error) {
      if (error instanceof HandBack && /(?:creature|pc)-death-not-modeled|lethal-no-death-tail/.test(error.reason))
        handBack('turn-tick:orb-pulse-may-kill');
      throw error;
    }
    if (!input.outOfCombat && upper(itemField(ending, 'item_type') ?? ec(ending).item_type) === 'LAIR') {
      for (const entry of input.endingEntries ?? []) {
        if (entry.max === null) {
          if (!entry.definition) effect('lair-refresh:' + entry.id, () => handBack('lair-refresh:definition-not-held'));
          continue;
        }
        effect('lair-refresh:' + entry.id, () => {
          const state = abilityChargeEntry(ctx, ending, entry.id);
          if (state.current !== state.max) adjustCharges(ctx, ending, entry.id, state.max - state.current, false);
        });
      }
    }
    if (!input.outOfCombat) insertRoll(ctx, { roll_type_1: 'end_turn', by: ending });
  }
  if (input.outOfCombat && ending && !Number(ecLean(ending, 'encounter_creature_dead'))) {
    effect('start-maintained-expiry', () => runMaintainedExpiry(ctx, ending, 'start'));
    effect('start-expiry-triggers', () => expiryTriggers(ctx, new Set(statusRows(ending, 'caster').map(s => upper(s.status_id)).filter(id => input.startRemoved.has(id))), input.startExpiries));
    effect('start-regeneration', () => statusRegeneration(ctx, ending, input.startRemoved, input.startExpiries, effect, false));
  }
  if (input.skipStart) {
    const result: TurnTickResult = { rows: ctx.rows, ec: ecChanges(ctx), statusesAdded: ctx.statusesAdded,
      statusesRemoved: ctx.statusesRemoved, statusesChanged: stackChanges(), charges: ctx.chargesChanged, ...radiusDelta,
      ...(ctx.teleportOffers?.length ? { teleportOffers: ctx.teleportOffers } : {}),
      ...(spawnPlanHasEffects(ctx.spawnRows) ? { spawnRows: ctx.spawnRows } : {}),
      ...(ctx.statusTriggerListeners?.length ? { statusTriggerListeners: ctx.statusTriggerListeners } : {}),
      ...(ctx.fortunateOffers?.size ? { fortunateOffers: [...ctx.fortunateOffers] } : {}) };
    assertTurnTickContract(result, 64);
    input.prepare?.(result);
    return result;
  }
  endPhase = false;
  const successor = input.nextAfterEnd?.([...members.values()]);
  if (successor) next = members.get(upper(successor.id)) ?? handBack('turn-tick:successor-not-held');
  const projected = projectStatuses(ctx, next);
  const start = input.deriveStart?.(projected, [...members.values()], successor);
  const reaction = start?.reset.reaction ?? input.nextReaction;
  const startRemoved = start ? new Set(start.removals.map(r => r.status_id)) : input.startRemoved;
  const startExpiries = start?.removals ?? input.startExpiries;
  // Preserve working row identity for nested casts; hydrate post-end statuses before all START hooks.
  next.statuses = projected.statuses;
  // OWNER RULING 2026-10-03: a player-controlled next actor still holding its reaction is asked BEFORE the refresh ("right before their turn
  // starts"); its rows land between the end_turn and start_turn markers
  const startMode = input.endingId === null && !input.resumedMain;
  if (input.resumedMain) insertRoll(ctx, { roll_type_1: 'end_turn', by: next });
  const fortunateDone = !startMode && effect('fortunate-before-refresh', () => fortunateBeforeRefresh(ctx, next, startRemoved));
  if (start) ec(next).aps = start.bare.aps;
  ec(next).reaction = 1;
  insertRoll(ctx, { roll_type_1: 'start_turn', by: next });
  startAbilityCharges(ctx, next, input.entriesFor?.(next) ?? input.entries, startRemoved, effect);
  effect('start-maintained-expiry', () => runMaintainedExpiry(ctx, next, 'start'));
  effect('start-expiry-triggers', () => expiryTriggers(ctx, new Set(statusRows(next, 'caster').map(s => upper(s.status_id)).filter(id => startRemoved.has(id))), startExpiries));
  effect('start-regeneration', () => statusRegeneration(ctx, next, startRemoved, startExpiries, effect));
  effect('orb-start', () => orbStartOfTurnChannelPass(ctx, next));
  next.statuses = projectStatuses(ctx, next).statuses;
  effect('turn-begin-triggers', () => turnBeginTriggers(ctx, next));
  // An unavailable authored listener must not suppress the independent universal
  // reaction. A pre-refresh offer, including a decline, remains the only offer.
  if (!fortunateDone && !deferStartModeFortunate(ctx, next, startMode))
    effect('fortunate-after-refresh', () => turnStartFortunate(ctx, next, 'triggers_when_caster_turn_begins'));
  const ecOut = ecChanges(ctx);
  if (start && ecOut[ecId(next)] && ec(next).aps === start.reset.aps) delete ecOut[ecId(next)]!.aps;
  if (start && ec(next).aps !== start.reset.aps) (ecOut[ecId(next)] ??= {}).aps = ec(next).aps;
  // The commit writes the tick's columns AFTER the next actor's reset (turn_truth_commit_inc.php), and ecChanges diffs against the STORED row:
  // a post-refresh Fortunate on a creature whose stored reaction was already 0 (0 → reset 1 → spent 0) left no `reaction` change, so the reset's
  // 1 stood although the reaction was spent (BUG LEDGER, lane A11). The tick's final reaction rides whenever it is not the reset's.
  const finalReaction = int(record(next.encounter_creatures)?.reaction ?? reaction);
  if (finalReaction !== reaction) (ecOut[ecId(next)] ??= {}).reaction = finalReaction;
  // Removing the AP refresh can exhaust this creature delta.
  for (const [id, columns] of Object.entries(ecOut)) if (!Object.keys(columns).length) delete ecOut[id];
  const result: TurnTickResult = { rows: ctx.rows, ec: ecOut, statusesAdded: ctx.statusesAdded,
    statusesRemoved: ctx.statusesRemoved, statusesChanged: stackChanges(), charges: ctx.chargesChanged, ...radiusDelta,
    ...(ctx.teleportOffers?.length ? { teleportOffers: ctx.teleportOffers } : {}),
    ...(start ? { start } : {}), ...(successor ? { successor } : {}),
    ...(spawnPlanHasEffects(ctx.spawnRows) ? { spawnRows: ctx.spawnRows } : {}),
      ...(ctx.statusTriggerListeners?.length ? { statusTriggerListeners: ctx.statusTriggerListeners } : {}),
    ...(ctx.fortunateOffers?.size ? { fortunateOffers: [...ctx.fortunateOffers] } : {}) };
  assertTurnTickContract(result, 64);
  input.prepare?.({ ...result, rows: [...result.rows, ...[...new Set(skipped.values())].map(unportedSkipRow)] });
  return result;
}
